import { useEffect, useMemo, useState } from 'react';
import apiClient from '../../utils/api';
import useListPagination from '../../hooks/useListPagination';
import PaginationBar from '../shared/PaginationBar';
import PlatformActivityChart from './PlatformActivityChart';
import BrowseListedDateFilter from '../listings/BrowseListedDateFilter';
import { ADMIN_DELETED_LISTINGS_PAGE_SIZE, COOWN_PROJECT_START_YEAR } from '../../config/constants';
import { formatUsd } from '../../data/countries';
import {
  getSubmissionListingMode,
  formatSubmissionListingModeLabel,
  formatSubmissionStatusLabel,
  getSubmissionDeletionActor,
  getSubmissionPhotoUrls,
} from '../../utils/submissions';
import {
  DELETED_LISTINGS_METRICS,
  DELETED_LISTINGS_CHART_COLORS,
  computeDeletedListingsMetrics,
  adminDeletedListingMatchesSearch,
  deletedMatchesDateFilter,
  downloadDeletedListingsCsv,
  getSubmissionDeletedAt,
} from '../../utils/adminDeletedListings';

const DeletedListingDetailModal = ({ row, restoring, onClose, onRestore }) => {
  if (!row) return null;
  const deletion = getSubmissionDeletionActor(row);
  const isRent = getSubmissionListingMode(row) === 'rent';
  const photos = getSubmissionPhotoUrls(row);
  const deletedAt = getSubmissionDeletedAt(row);
  const deletedLabel = deletedAt
    ? deletedAt.toLocaleString()
    : row.deletedDate || '—';

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && !restoring && onClose?.()}>
      <div className="modal admin-deleted-listing-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="deleted-listing-detail-title">
        <div className="admin-deleted-listing-modal__header">
          <div>
            <span className={`status-badge listing-mode-badge status-${isRent ? 'pending' : 'approved'}`}>
              {formatSubmissionListingModeLabel(row, { short: true })}
            </span>
            <h2 id="deleted-listing-detail-title">{row.title || 'Deleted listing'}</h2>
            <p className="admin-deleted-listing-modal__subtitle">{row.location || '—'}</p>
            <p className="admin-deleted-listing-modal__subtitle">
              Deleted {deletedLabel}
              {' · '}
              {deletion.role === 'admin' ? 'By admin' : 'By lister'}
              {deletion.email ? ` (${deletion.email})` : ''}
            </p>
          </div>
          <button type="button" className="modal-close" disabled={restoring} onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body admin-deleted-listing-modal__body">
          <div className="admin-deleted-listing-modal__details">
            <div><strong>Submission ID:</strong> {row.id}</div>
            <div><strong>Lister:</strong> {row.agentEmail || row.submitterEmail || '—'}</div>
            <div><strong>Type:</strong> {row.type || '—'}</div>
            <div><strong>Pipeline status:</strong> {formatSubmissionStatusLabel(row.status)}</div>
            <div>
              <strong>Price:</strong>{' '}
              {isRent && Number(row.monthlyRent) > 0
                ? `${formatUsd(Number(row.monthlyRent))}/mo`
                : `$${Number(row.price || 0).toLocaleString()}`}
            </div>
            <div><strong>Published property:</strong> {row.publishedPropertyId || '—'}</div>
          </div>
          {photos.length > 0 ? (
            <div className="admin-deleted-listing-modal__photos">
              <h3><i className="fas fa-images"></i> Photos</h3>
              <div className="admin-deleted-listing-modal__photo-grid">
                {photos.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt="" />
                  </a>
                ))}
              </div>
            </div>
          ) : null}
          <div className="admin-deleted-listing-modal__actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={restoring}
              onClick={() => onRestore?.(row)}
            >
              <i className={`fas fa-undo${restoring ? ' fa-spin' : ''}`}></i>
              {restoring ? ' Restoring…' : ' Restore listing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDeletedListingsPanel = ({ addToast, onCountChange, onRestored }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState('all');
  const [dateMode, setDateMode] = useState('all');
  const [deletedYear, setDeletedYear] = useState('');
  const [deletedMonth, setDeletedMonth] = useState('');
  const [deletedDate, setDeletedDate] = useState('');
  const [chartType, setChartType] = useState('pie');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.getDeletedSubmissions();
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      onCountChange?.(data.length);
    } catch (err) {
      addToast(err.message || 'Could not load deleted listings.', 'error');
      setRows([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => rows.filter((row) => {
    if (modeFilter !== 'all' && getSubmissionListingMode(row) !== modeFilter) return false;
    if (!deletedMatchesDateFilter(row, {
      mode: dateMode,
      year: deletedYear,
      month: deletedMonth,
      date: deletedDate,
    })) return false;
    return adminDeletedListingMatchesSearch(row, search);
  }), [rows, modeFilter, dateMode, deletedYear, deletedMonth, deletedDate, search]);

  const metrics = useMemo(() => computeDeletedListingsMetrics(filteredRows), [filteredRows]);
  const modeCounts = useMemo(() => ({
    all: rows.length,
    sale: rows.filter((row) => getSubmissionListingMode(row) === 'sale').length,
    rent: rows.filter((row) => getSubmissionListingMode(row) === 'rent').length,
  }), [rows]);

  const filtersActive = search.trim() !== ''
    || modeFilter !== 'all'
    || dateMode !== 'all'
    || Boolean(deletedYear || deletedMonth || deletedDate);
  const paginationKey = `deleted|${modeFilter}|${search}|${dateMode}|${deletedYear}|${deletedMonth}|${deletedDate}`;
  const {
    items: pageRows,
    page,
    total,
    totalPages,
    startIndex,
    endIndex,
    hasPrev,
    hasNext,
    goToPage,
  } = useListPagination(filteredRows, paginationKey, ADMIN_DELETED_LISTINGS_PAGE_SIZE);

  const selectedRow = rows.find((row) => row.id === selectedId) || null;

  const handleExport = () => {
    setExporting(true);
    try {
      downloadDeletedListingsCsv(rows);
      addToast('Deleted listings CSV downloaded.', 'success');
    } catch (err) {
      addToast(err.message || 'CSV export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleRestore = async (sub) => {
    if (!window.confirm(`Restore "${sub.title}"? The lister will see it again under My Listings.`)) return;
    setRestoringId(sub.id);
    try {
      const res = await apiClient.restoreSubmission(sub.id);
      const reactivated = Boolean(res.data?.propertyReactivated);
      addToast(
        reactivated
          ? 'Listing restored and returned to the public site.'
          : 'Listing restored to the pipeline (re-publish from Submissions if it was Live on Site).',
        'success'
      );
      setRows((prev) => {
        const next = prev.filter((row) => row.id !== sub.id);
        onCountChange?.(next.length);
        return next;
      });
      setSelectedId(null);
      if (onRestored) await onRestored();
    } catch (err) {
      addToast(err.message || 'Restore failed.', 'error');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-trash-alt"></i> Deleted listings</h3>
          <p className="admin-panel-sub">
            Compact queue — click a row to open details and restore. CSV export includes the full catalog.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={handleExport} disabled={exporting || rows.length === 0}>
            <i className={`fas fa-download${exporting ? ' fa-spin' : ''}`}></i> Download CSV (all)
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            <i className={`fas fa-sync${loading ? ' fa-spin' : ''}`}></i> Refresh
          </button>
        </div>
      </div>

      <div className="platform-activity-layout live-listings-analytics">
        <div className="detail-metrics platform-activity-metrics">
          {DELETED_LISTINGS_METRICS.map((metric) => (
            <div key={metric.key} className="detail-metric">
              <label>{metric.label}</label>
              <value>{Number(metrics[metric.key] || 0).toLocaleString()}</value>
            </div>
          ))}
        </div>
        <PlatformActivityChart
          metrics={metrics}
          metricDefs={DELETED_LISTINGS_METRICS}
          chartType={chartType}
          onChartTypeChange={setChartType}
          title="Deleted catalog breakdown"
          emptyMessage="No deleted listings yet."
          ariaLabel="Deleted listings breakdown"
          colorMap={DELETED_LISTINGS_CHART_COLORS}
        />
      </div>

      {filtersActive ? (
        <p className="live-listings-analytics-note">
          Metrics and chart reflect <strong>{filteredRows.length.toLocaleString()}</strong> filtered listings.
          CSV export still includes the full catalog ({rows.length.toLocaleString()}).
        </p>
      ) : null}

      <div className="live-listings-toolbar" aria-label="Browse deleted listings">
        <form className="live-listings-toolbar__search submission-queue-search" onSubmit={(e) => e.preventDefault()}>
          <div className="submission-queue-search-wrap">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="search"
              placeholder="Search title, location, lister, status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search deleted listings"
            />
            {search ? (
              <button type="button" className="submission-queue-search-clear" aria-label="Clear search" onClick={() => setSearch('')}>
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            ) : null}
          </div>
        </form>
        <div className="live-listings-toolbar__grid">
          <div className="live-listings-toolbar__group">
            <span className="live-listings-toolbar__label">Listing type</span>
            <div className="listings-view-toggle" role="tablist" aria-label="Deleted listing type">
              {[
                { key: 'all', label: `All (${modeCounts.all})` },
                { key: 'sale', label: `Fractional buy (${modeCounts.sale})` },
                { key: 'rent', label: `For rent (${modeCounts.rent})` },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={modeFilter === tab.key}
                  className={modeFilter === tab.key ? 'active' : ''}
                  onClick={() => setModeFilter(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <BrowseListedDateFilter
            variant="admin"
            fieldLabel="Deleted"
            yearMin={COOWN_PROJECT_START_YEAR}
            useCalendarInputs
            dateMode={dateMode}
            onDateModeChange={setDateMode}
            listedYear={deletedYear}
            onListedYearChange={setDeletedYear}
            listedMonth={deletedMonth}
            onListedMonthChange={setDeletedMonth}
            listedDate={deletedDate}
            onListedDateChange={setDeletedDate}
            onClear={() => {
              setDeletedYear('');
              setDeletedMonth('');
              setDeletedDate('');
            }}
          />
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="investment-table investment-table--deleted-listings">
          <thead>
            <tr>
              <th>Deal</th>
              <th>Listing</th>
              <th>Type</th>
              <th>Target</th>
              <th>Pipeline</th>
              <th>Deleted by</th>
              <th>Deleted</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  Loading deleted listings…
                </td>
              </tr>
            ) : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  No deleted listings.
                </td>
              </tr>
            ) : null}
            {!loading && rows.length > 0 && total === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  No listings match your filters.
                </td>
              </tr>
            ) : null}
            {pageRows.map((row) => {
              const isRent = getSubmissionListingMode(row) === 'rent';
              const deletion = getSubmissionDeletionActor(row);
              const deletedAt = getSubmissionDeletedAt(row);
              const deletedLabel = deletedAt
                ? deletedAt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                : row.deletedDate || '—';
              const dealMeta = row.location || '';
              return (
                <tr
                  key={row.id}
                  className={`admin-deleted-listing-row${selectedId === row.id ? ' is-selected' : ''}`}
                  onClick={() => setSelectedId(row.id)}
                >
                  <td className="live-listing-deal-cell">
                    <div className="live-listing-deal-line">
                      <strong className="live-listing-deal-title">{row.title}</strong>
                      {row.publishedPropertyId ? (
                        <span className="admin-listing-badge admin-listing-badge--live live-listing-deal-badge">Was live</span>
                      ) : null}
                    </div>
                    {dealMeta ? <div className="live-listing-deal-sub">{dealMeta}</div> : null}
                  </td>
                  <td>
                    <span className={`status-badge listing-mode-badge status-${isRent ? 'pending' : 'approved'}`}>
                      {formatSubmissionListingModeLabel(row, { short: true })}
                    </span>
                  </td>
                  <td>{row.type || '—'}</td>
                  <td className="live-listing-target-cell">
                    {isRent && Number(row.monthlyRent) > 0 ? (
                      <span className="live-listing-target-main">{formatUsd(Number(row.monthlyRent))}/mo</span>
                    ) : (
                      <span className="live-listing-target-main">${Number(row.price || 0).toLocaleString()}</span>
                    )}
                  </td>
                  <td className="admin-deleted-listing-pipeline-cell">{formatSubmissionStatusLabel(row.status)}</td>
                  <td className="admin-deleted-listing-actor-cell">
                    <div className="admin-deleted-listing-actor-stack">
                      <span className={`status-badge ${deletion.role === 'admin' ? 'deletion-actor-badge--admin' : 'deletion-actor-badge--lister'}`}>
                        {deletion.role === 'admin' ? 'Admin' : 'Lister'}
                      </span>
                      {deletion.email ? (
                        <div className="live-listing-deal-sub admin-deleted-listing-row__email">{deletion.email}</div>
                      ) : null}
                    </div>
                  </td>
                  <td>{deletedLabel}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm live-listing-manage-btn"
                      onClick={(e) => { e.stopPropagation(); setSelectedId(row.id); }}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.length > 0 ? (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          total={total}
          startIndex={startIndex}
          endIndex={endIndex}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPageChange={goToPage}
          itemLabel="listings"
          compact
        />
      ) : null}

      <DeletedListingDetailModal
        row={selectedRow}
        restoring={restoringId === selectedRow?.id}
        onClose={() => setSelectedId(null)}
        onRestore={handleRestore}
      />
    </div>
  );
};

export default AdminDeletedListingsPanel;
